
import { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Profile } from '@/types/database.types';
import { filterUsers } from '../utils/userHelpers';
import { useUserData } from './useUserData';
import { useUserFiltering } from './useUserFiltering';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import { useDebugMode } from './useDebugMode';
import { useAuth } from '@/contexts/AuthContext';

export const useUserManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  const { user } = useAuth();
  const { isSuperAdmin, isLoading: isAdminLoading } = useAdminStatus();
  
  // Use specialized hooks
  const { 
    users, 
    recentUsers,
    isError, 
    error, 
    refetch,
    isLoading: isDataLoading
  } = useUserData();

  const { filteredUsers } = useUserFiltering(users, searchQuery);
  
  // Get admin users from the users array
  const adminUsers = users?.filter(user => user.user_type === 'Super Admin') || [];
  const adminUserIds = adminUsers.map(user => user.id);
  
  const handleAdminStatusChange = () => {
    if (!isSuperAdmin) {
      toast({
        title: "Access Restricted",
        description: "Only super admins can modify admin status",
        variant: "destructive"
      });
      return;
    }
    refetch();
  };
  
  const { showDebugInfo, toggleDebugInfo, debugData } = useDebugMode({
    users,
    filteredUsers,
    recentUsers,
    searchQuery,
    adminUsers: adminUserIds,
    error
  });

  const handleRefresh = () => {
    toast({
      title: "Refreshing data",
      description: "Fetching the latest user data"
    });
    refetch();
  };

  return {
    users,
    filteredUsers,
    recentUsers,
    adminUsers,
    adminUserIds,
    searchQuery,
    setSearchQuery,
    isLoading: isDataLoading || isAdminLoading,
    isError,
    error,
    refetch,
    handleAdminStatusChange,
    handleRefresh,
    showDebugInfo,
    toggleDebugInfo,
    debugData,
    isSuperAdmin
  };
};
